import {arrowSvg, createSvgEl} from "../RouteTechniques/Util/Svg";

export type CardinalDirectionsLocale = 'nl' | 'en';

export class CardinalDirections {

    private static readonly DIRECTIONS: [number, string, string][] = [
        [0, 'N'   , 'N'   ],
        [3, 'NbE' , 'NtO' ],
        [2, 'NNE' , 'NNO' ],
        [3, 'NEbN', 'NOtN'],
        [1, 'NE'  , 'NO'  ],
        [3, 'NEbE', 'NOtO'],
        [2, 'ENE' , 'ONO' ],
        [3, 'EbN' , 'OtN' ],
        [0, 'E'   , 'O'   ],
        [3, 'EbS' , 'OtZ' ],
        [2, 'ESE' , 'OZO' ],
        [3, 'SEbE', 'ZOtO'],
        [1, 'SE'  , 'ZO'  ],
        [3, 'SEbS', 'ZOtZ'],
        [2, 'SSE' , 'ZZO' ],
        [3, 'SbE' , 'ZtO' ],
        [0, 'S'   , 'Z'   ],
        [3, 'SbW' , 'ZtW' ],
        [2, 'SSW' , 'ZZW' ],
        [3, 'SWbS', 'ZWtZ'],
        [1, 'SW'  , 'ZW'  ],
        [3, 'SWbW', 'ZWtW'],
        [2, 'WSW' , 'WZW' ],
        [3, 'WbS' , 'WtZ' ],
        [0, 'W'   , 'W'   ],
        [3, 'WbN' , 'WtN' ],
        [2, 'WNW' , 'WNW' ],
        [3, 'NWbW', 'NWtW'],
        [1, 'NW'  , 'NW'  ],
        [3, 'NWbN', 'NWtN'],
        [2, 'NNW' , 'NNW' ],
        [3, 'NbW' , 'NtW' ],
    ];

    private static readonly LOCALE_MAPPING: Record<string, number> = {
        'en': 1,
        'nl': 2,
    }

    public static readonly LOCALES: Record<string, string> = {
        'en': 'Engels',
        'nl': 'Nederlands',
    }

    private readonly directions: [number, number, string][];

    public static readonly VALID_COUNTS = [4, 8, 16, 32];
    public static readonly DEFAULT_COUNT = 16;
    public static readonly DEFAULT_LOCALE: CardinalDirectionsLocale = 'nl';

    constructor(public readonly count: number, public readonly locale: CardinalDirectionsLocale) {
        if (!CardinalDirections.VALID_COUNTS.includes(this.count)) {
            throw new Error('Invalid count: ' + this.count);
        }

        if (typeof CardinalDirections.LOCALE_MAPPING[this.locale] === 'undefined') {
            throw new Error('Invalid locale: ' + this.locale);
        }
        const locale_index = CardinalDirections.LOCALE_MAPPING[this.locale];

        const level = Math.log2(count) - 2;
        const directions = CardinalDirections.DIRECTIONS.filter(
            (direction: [number, string, string], index: number) => direction[0] <= level,
        );

        this.directions = directions.map(
            (direction: [number, string, string], index: number) => [direction[0], 360 / count * index, <string>direction[locale_index]]
        );
    }

    public bearingToDirection(bearing: number): string {
        const degree_step = 360 / this.count;

        const offset = bearing % degree_step;

        let direction_index = (bearing - offset) / degree_step;
        if (offset > degree_step / 2) {
            direction_index += 1;
        }
        if (direction_index == this.count) {
            direction_index = 0;
        }

        return this.directions[direction_index][2];
    }

    public getExampleSvg(size_px: number): SVGSVGElement {
        const padding = 0.05;
        const size = 1.2;
        const svg = createSvgEl('svg', {
            width: size_px + 'px',
            height: size_px + 'px',
            viewBox: (-padding) + " " + (-padding - size) + " " + (size + 2 * padding) + " " + (size + 2 * padding),
        });

        const directions = this.directions.filter(
            ([level, degrees, cardinalName]) => degrees <= 90,
        );

        const radii = {
            0: [0, 1.05],
            1: [0, 1],
            2: [0, 1],
            3: [0.98, 1.02],
        };

        svg.appendChild(createSvgEl('path', {
            stroke: '#000000',
            strokeWidth: 0.005,
            fill: 'none',
            d: 'M0,-1 a1,1 0 0,1 1,1'
        }));

        for (const [level, degrees, cardinalName] of directions) {
            const x_norm = Math.cos((90 - degrees) / 180 * Math.PI);
            const y_norm = -Math.sin((90 - degrees) / 180 * Math.PI);

            const [r1, r2] = radii[level];

            if (level == 0 || level == 1) {
                svg.appendChild(arrowSvg(
                    x_norm * r1,
                    y_norm * r1,
                    x_norm * r2,
                    y_norm * r2,
                    {
                        stroke: '#000000',
                        strokeWidth: 0.005,
                        fill: level == 0 ? '#000000' : 'none',
                    },
                ))
            } else {
                const attr = {
                    x1: x_norm * r1,
                    y1: y_norm * r1,
                    x2: x_norm * r2,
                    y2: y_norm * r2,
                    stroke: '#000000',
                    strokeWidth: 0.005,
                };

                if (level === 2) {
                    attr['stroke-dasharray'] = '0.05 0.025';
                }

                svg.appendChild(createSvgEl('line', attr));
            }

            const text = createSvgEl('text', {
                x: x_norm * (r2 + 0.02),
                y: y_norm * (r2 + 0.02),
                textAnchor: 'start',
                dominantBaseline: 'text-bottom',
                color: 'black',
            });
            text.textContent = cardinalName;
            text.style.font = (0.1 - 0.01 * level) + 'px sans-serif';
            svg.appendChild(text);
        }

        return svg;
    }

    public getExampleSvgString(size_px: number): string {
        return (new XMLSerializer()).serializeToString(this.getExampleSvg(size_px));
    }
}
